import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  REPEAT_FREQS,
  WEEKDAY_ORDER,
  WEEKDAY_SHORT,
  describeRepeat,
  repeatUnit,
  type RepeatFreq,
  type RepeatRule,
} from '../../lib/recurrence';
import { Input, Select, FOCUS_RING } from '../ui';
import { DateInput } from '../DateInput';

const FREQ_LABELS: Record<RepeatFreq, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
};

/**
 * The Repeat property: a unit, then -- once there is one -- how often, which
 * days, and when it stops.
 *
 * Inline rather than a popover. Every other control in the property sheet
 * writes through as it changes, and a popover with its own Apply would be the
 * one place in the panel where an edit could be lost by clicking away. The
 * extra controls only appear once the task repeats, so a task that does not
 * pays one select for it.
 *
 * Read-only once the instance has spawned: the rule is copied forward, so on
 * an old instance it is history, and the server refuses a change to it. The
 * link goes where the edit belongs.
 */
export function RepeatPicker({
  id,
  value,
  nextInstanceHref,
  onChange,
}: {
  id: string;
  value: RepeatRule | null;
  /** Set when this instance has already spawned its successor. */
  nextInstanceHref?: string | null;
  onChange: (rule: RepeatRule | null) => void;
}) {
  if (value && nextInstanceHref) {
    return (
      <p className="flex min-h-[1.75rem] flex-wrap items-center gap-x-2 text-dense text-stone-700">
        {describeRepeat(value)}
        <Link to={nextInstanceHref} className="text-meta font-medium text-honey-700 hover:underline">
          Next instance →
        </Link>
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <Select
        compact
        id={id}
        value={value?.freq ?? ''}
        onChange={(event) => {
          const freq = event.target.value as RepeatFreq | '';
          if (!freq) {
            onChange(null);
            return;
          }
          // The interval and the end survive a change of unit; the weekdays
          // only mean anything to a weekly rule.
          onChange({
            freq,
            interval: value?.interval ?? 1,
            weekdays: freq === 'WEEKLY' ? (value?.weekdays ?? []) : [],
            until: value?.until ?? null,
          });
        }}
      >
        <option value="">Doesn’t repeat</option>
        {REPEAT_FREQS.map((freq) => (
          <option key={freq} value={freq}>
            {FREQ_LABELS[freq]}
          </option>
        ))}
      </Select>

      {value ? (
        <>
          <div className="flex items-center gap-1.5 text-dense text-stone-600">
            Every
            <div className="w-14">
              <IntervalInput
                aria-label="Repeat interval"
                value={value.interval}
                onCommit={(interval) => onChange({ ...value, interval })}
              />
            </div>
            {repeatUnit(value.freq, value.interval)}
          </div>

          {value.freq === 'WEEKLY' ? (
            <div role="group" aria-label="Repeat on" className="flex flex-wrap gap-1">
              {WEEKDAY_ORDER.map((day) => {
                const on = value.weekdays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      onChange({
                        ...value,
                        weekdays: on
                          ? value.weekdays.filter((entry) => entry !== day)
                          : [...value.weekdays, day].sort((a, b) => a - b),
                      })
                    }
                    className={`rounded-md px-1.5 py-0.5 text-meta font-medium transition ${FOCUS_RING} ${
                      on
                        ? 'bg-honey-100 text-honey-800'
                        : 'bg-white text-stone-500 ring-1 ring-inset ring-stone-200 hover:text-stone-900'
                    }`}
                  >
                    {WEEKDAY_SHORT[day]}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="flex items-center gap-1.5 text-dense text-stone-600">
            Until
            <div className="min-w-0 flex-1">
              <DateInput
                compact
                aria-label="Repeat until"
                value={value.until ?? ''}
                onCommit={(until) => onChange({ ...value, until: until || null })}
              />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * A whole number of at least one, committed on blur or Enter rather than per
 * keystroke -- every commit is a save, and typing "12" would otherwise save a
 * 1 on the way.
 */
function IntervalInput({
  value,
  onCommit,
  'aria-label': ariaLabel,
}: {
  value: number;
  onCommit: (value: number) => void;
  'aria-label': string;
}) {
  const [draft, setDraft] = useState(String(value));
  useEffect(() => setDraft(String(value)), [value]);

  function commit() {
    const parsed = Math.min(365, Math.max(1, Math.round(Number(draft))));
    if (!Number.isFinite(parsed) || parsed === value) {
      setDraft(String(value));
      return;
    }
    onCommit(parsed);
  }

  return (
    <Input
      compact
      type="number"
      inputMode="numeric"
      min={1}
      max={365}
      aria-label={ariaLabel}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
      }}
    />
  );
}
